import { useSeason } from '../../context/SeasonContext';
import { seasonYear } from '../../utils/seasonDates';
import { useEffect, useMemo, useState } from 'react';
import { getScheduleEntries } from '../../api/schedule';
import { ArtifactModal } from '../../components/ArtifactModal/ArtifactModal';
import styles from './SchedulePage.module.css';

const MONTHS = [
  'января',
  'февраля',
  'марта',
  'апреля',
  'мая',
  'июня',
  'июля',
  'августа',
  'сентября',
  'октября',
  'ноября',
  'декабря',
];

const WEEKDAYS = [
  'Воскресенье',
  'Понедельник',
  'Вторник',
  'Среда',
  'Четверг',
  'Пятница',
  'Суббота',
];

function parseEntryDate(rawLabel, yearHint) {
  const label = String(rawLabel || '').trim().toLowerCase();
  if (!label) return null;

  const dottedMatch = label.match(/(\d{1,2})\.(\d{1,2})(?:\.(\d{2,4}))?/);
  if (dottedMatch) {
    const day = Number(dottedMatch[1]);
    const month = Number(dottedMatch[2]);
    const year = Number(dottedMatch[3] || yearHint);
    const normalizedYear = year < 100 ? 2000 + year : year;
    const parsed = new Date(normalizedYear, month - 1, day);
    if (!Number.isNaN(parsed.getTime())) return parsed;
  }

  const monthPattern = new RegExp(`(\\d{1,2})\\s+(${MONTHS.join('|')})(?:\\s+(\\d{4}))?`, 'i');
  const namedMatch = label.match(monthPattern);
  if (namedMatch) {
    const day = Number(namedMatch[1]);
    const month = MONTHS.findIndex((monthName) => monthName === namedMatch[2].toLowerCase()) + 1;
    const year = Number(namedMatch[3] || yearHint);
    const parsed = new Date(year, month - 1, day);
    if (!Number.isNaN(parsed.getTime())) return parsed;
  }

  return null;
}

function getWeekStart(date) {
  const copy = new Date(date);
  const day = copy.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  copy.setDate(copy.getDate() + diff);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

function formatWeekRange(weekStart) {
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekEnd.getDate() + 6);

  const startDay = weekStart.getDate();
  const endDay = weekEnd.getDate();
  const startMonth = MONTHS[weekStart.getMonth()];
  const endMonth = MONTHS[weekEnd.getMonth()];

  if (weekStart.getMonth() === weekEnd.getMonth()) {
    return `${startDay}–${endDay} ${startMonth}`;
  }

  return `${startDay} ${startMonth} — ${endDay} ${endMonth}`;
}

function buildDateMeta(entry, yearHint) {
  const parsedDate = parseEntryDate(entry.date_label, yearHint);
  if (parsedDate) {
    return {
      parsedDate,
      weekdayLabel: WEEKDAYS[parsedDate.getDay()],
      dayLabel: String(parsedDate.getDate()),
      monthLabel: MONTHS[parsedDate.getMonth()],
      weekKey: getWeekStart(parsedDate).toISOString(),
    };
  }

  return {
    parsedDate: null,
    weekdayLabel: entry.track_label || 'Событие',
    dayLabel: '—',
    monthLabel: entry.date_label || 'Дата уточняется',
    weekKey: `fallback:${entry.date_label || entry.entry_id}`,
  };
}

function isPastEntry(parsedDate) {
  if (!parsedDate) return false;
  const endOfDay = new Date(parsedDate);
  endOfDay.setHours(23, 59, 59, 999);
  return endOfDay.getTime() < Date.now();
}

function normalizeTrackId(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/[^a-zа-я0-9]+/gi, '-')
    .replace(/^-+|-+$/g, '');
}

export function SchedulePage() {
  const { season } = useSeason();
  const yearHint = seasonYear(season);
  const [entries, setEntries] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [query, setQuery] = useState('');
  const [activeTrack, setActiveTrack] = useState('ALL');
  const [openEntries, setOpenEntries] = useState(() => new Set());
  const [artifactEntry, setArtifactEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showPast, setShowPast] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    getScheduleEntries()
      .then((data) => {
        if (cancelled) return;
        setEntries(Array.isArray(data?.items) ? data.items : []);
        setTracks(Array.isArray(data?.tracks) ? data.tracks : []);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message || 'Не удалось загрузить расписание.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const trackOptions = useMemo(() => {
    const seen = new Set();
    return tracks.filter((track) => {
      const key = String(track || '').trim().toLowerCase();
      if (!key || seen.has(key)) return false;
      if (key === 'все треки' || key === 'all') return false;
      seen.add(key);
      return true;
    });
  }, [tracks]);

  // const trackOptions = useMemo(() => {
  //   const seen = new Set();
  //   return tracks.filter((track) => {
  //     const key = String(track || '').trim().toLowerCase();
  //     if (!key || seen.has(key)) return false;
  //     if (key === 'все треки' || key === 'all') return false; // исключаем
  //     seen.add(key);
  //     return true;
  //   });
  // }, [tracks]);

  const filteredEntries = useMemo(() => {
    const pattern = query.trim().toLowerCase();
    return entries.filter((entry) => {
      const entryTrack = String(entry.track_label || '').trim().toLowerCase();
      
      if (activeTrack !== 'ALL') {
        const normalizedActiveTrack = activeTrack.toLowerCase();
        if (entryTrack !== normalizedActiveTrack && entryTrack !== 'все треки') {
          return false;
        }
      }
      if (!pattern) return true;
      const haystack = [
        entry.track_label,
        entry.date_label,
        entry.time_label,
        entry.title,
        entry.description,
        entry.presenter_name,
        entry.location,
        entry.format_label,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(pattern);
    });
  }, [entries, query, activeTrack]);

  const normalizedEntries = useMemo(
    () =>
      filteredEntries.map((entry, index) => ({
        entry,
        index,
        dateMeta: buildDateMeta(entry, yearHint),
      })),
    [filteredEntries, yearHint],
  );

  function getFormatBorderColor(formatLabel) {
      if (!formatLabel) return '#D9D9D9';
      const label = formatLabel.toLowerCase().trim();
      if (label === 'запись') return '#E3A763';
      if (label === 'онлайн' || label === 'онлайн / запись') return '#4C56E6';
      if (label === 'смешанный' || label === 'смешанный / запись') return '#8A38F5';
      if (label === 'очно') return '#EF4444';
      if (label === 'очно / запись') return '#22C55E';
      return '#D9D9D9';
  }

  const { upcomingEntries, pastEntries } = useMemo(() => {
    const upcoming = [];
    const past = [];
    normalizedEntries.forEach((item) => {
      if (isPastEntry(item.dateMeta.parsedDate)) past.push(item);
      else upcoming.push(item);
    });
    return { upcomingEntries: upcoming, pastEntries: past };
  }, [normalizedEntries]);

  const buildWeekSections = (items, weekOffset = 0) => {
    const sortedItems = [...items].sort((a, b) => {
      if (a.dateMeta.parsedDate && b.dateMeta.parsedDate) {
        return a.dateMeta.parsedDate.getTime() - b.dateMeta.parsedDate.getTime() || a.entry.sort_order - b.entry.sort_order;
      }
      if (a.dateMeta.parsedDate) return -1;
      if (b.dateMeta.parsedDate) return 1;
      return a.entry.sort_order - b.entry.sort_order || a.index - b.index;
    });

    const sections = [];
    const sectionIndexByKey = new Map();

    sortedItems.forEach((item) => {
      const { dateMeta } = item;
      if (dateMeta.parsedDate) {
        const weekKey = dateMeta.weekKey;
        let sectionIndex = sectionIndexByKey.get(weekKey);
        if (sectionIndex === undefined) {
          const weekStart = getWeekStart(dateMeta.parsedDate);
          sectionIndex = sections.length;
          sectionIndexByKey.set(weekKey, sectionIndex);
          sections.push({
            key: weekKey,
            label: `Неделя ${weekOffset + sections.length + 1}`,
            rangeLabel: formatWeekRange(weekStart),
            items: [],
          });
        }
        sections[sectionIndex].items.push(item);
        return;
      }

      if (!sections.length || sections[sections.length - 1].key !== 'fallback') {
        sections.push({
          key: 'fallback',
          label: `Неделя ${weekOffset + sections.length + 1}`,
          rangeLabel: 'Даты уточняются',
          items: [],
        });
      }
      sections[sections.length - 1].items.push(item);
    });

    return sections;
  };

  const pastWeekCount = useMemo(() => {
    const weekKeys = new Set();
    pastEntries.forEach(item => {
      if (item.dateMeta.parsedDate) {
        weekKeys.add(item.dateMeta.weekKey);
      }
    });
    return weekKeys.size;
  }, [pastEntries]);

const pastSections = useMemo(() => buildWeekSections(pastEntries, 0), [pastEntries]);
const weekSections = useMemo(() => buildWeekSections(upcomingEntries, pastWeekCount), [upcomingEntries, pastWeekCount]);

  const toggleEntry = (entryId) => {
    setOpenEntries((prev) => {
      const next = new Set(prev);
      if (next.has(entryId)) next.delete(entryId);
      else next.add(entryId);
      return next;
    });
  };

  const handleOpenArtifact = (entry) => {
    setArtifactEntry(entry);
  };

  return (
    <div className={styles.page}>
      <section className={styles.topRow}>
        <div className={styles.titleBlock}>
          <h1 className={styles.title}>Расписание</h1>
          <p className={styles.subtitle}>Календарь встреч и модулей ЮФУ.Про по неделям программы</p>
        </div>

        <div className={styles.controlsBlock}>
          <input
            className={styles.search}
            type="search"
            placeholder="Поиск по теме, спикеру, треку или формату"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <div className={styles.statsPill}>
            <span>Событий</span>
            <strong>{filteredEntries.length}</strong>
          </div>
        </div>
      </section>

      <section className={styles.trackFilters}>
        <button
          type="button"
          className={`${styles.trackButton} ${activeTrack === 'ALL' ? styles.trackButtonActive : ''}`}
          onClick={() => setActiveTrack('ALL')}
        >
          Все мероприятия
        </button>
        {trackOptions.map((track) => (
          <button
            key={track}
            type="button"
            className={`${styles.trackButton} ${activeTrack === track ? styles.trackButtonActive : ''}`}
            onClick={() => setActiveTrack(track)}
          >
            {track}
          </button>
        ))}
      </section>

      {error ? <div className={styles.error}>{error}</div> : null}
      {loading ? <div className={styles.empty}>Загружаем расписание…</div> : null}
      {!loading && !filteredEntries.length ? <div className={styles.empty}>По текущему фильтру события не найдены.</div> : null}

      {!loading && pastEntries.length ? (
        <div className={styles.pastToggleRow}>
          <button
            type="button"
            className={`${styles.pastToggle} ${showPast ? styles.pastToggleActive : ''}`}
            onClick={() => setShowPast((prev) => !prev)}
          >
            {showPast ? 'Скрыть прошедшие события' : 'Показать прошедшие события'}
            <span className={styles.pastCount}>{pastEntries.length}</span>
          </button>
        </div>
      ) : null}

      {!loading && showPast && pastSections.length ? (
        <section className={styles.weeks}>
          <div className={styles.pastHeader}>
            <h2 className={styles.weekTitle}>Прошедшие события</h2>
            <span className={styles.weekCount}>{pastEntries.length}</span>
          </div>
          {pastSections.map((week) => (
            <div key={`past-${week.key}`} className={styles.weekSection}>
              <div className={styles.weekHeader}>
                <div>
                  <h3 className={styles.weekTitle}>{week.label}</h3>
                  <p className={styles.weekRange}>{week.rangeLabel}</p>
                </div>
                <span className={styles.weekCount}>{week.items.length}</span>
              </div>

              <div className={styles.weekList}>
                {week.items.map(({ entry, dateMeta }) => {
                  const isOpen = openEntries.has(entry.entry_id);

                  const borderColor = getFormatBorderColor(entry.format_label);
                  const cardStyle = borderColor ? { borderLeft: `8px solid ${borderColor}` } : {};
                  return (
                    <article
                      key={`past-${entry.entry_id}`}
                      className={`${styles.card} ${isOpen ? styles.cardOpen : ''} ${styles.cardPast}`}
                      id={normalizeTrackId(`past-${week.key}-${entry.track_label}-${entry.entry_id}`)}
                    >
                      <button
                        type="button"
                        className={styles.cardToggle}
                        onClick={() => toggleEntry(entry.entry_id)}
                        aria-expanded={isOpen}
                      >
                        <div className={styles.datePane}>
                          <span className={styles.weekday}>{dateMeta.weekdayLabel}</span>
                          <strong className={styles.dayNumber}>{dateMeta.dayLabel}</strong>
                          <span className={styles.monthLabel}>{dateMeta.monthLabel}</span>
                        </div>

                        <div style={cardStyle} className={styles.cardBody}>
                          {entry.presenter_name ? <span className={styles.speakerLine}>{entry.presenter_name}</span> : null}
                          <div className={styles.cardTopline}>
                            <div className={styles.cardMeta}>
                              <span className={styles.trackChip}>{entry.track_label || 'ЮФУ.Про'}</span>
                              {entry.is_control_event ? <span className={styles.controlEvent}>Контрольное мероприятие</span> : null}
                            </div>
                            <span className={styles.cardArrow} aria-hidden="true" />
                          </div>
                          <strong className={styles.cardTitle}>{entry.title}</strong>
                          <span className={styles.timeLabel}>
                            {entry.time_label || 'Время уточняется'}
                          </span>
                          {entry.description ? <p className={styles.cardLead}>{entry.description}</p> : null}
                        </div>
                      </button>

                      <div className={`${styles.cardDetails} ${isOpen ? styles.cardDetailsOpen : ''}`}>
                        <div className={styles.infoGrid}>
                          <div className={styles.infoCard}>
                            <span>Координатор</span>
                            <strong>{entry.presenter_name || 'Организаторы программы'}</strong>
                          </div>
                          <div className={styles.infoCard}>
                            <span>Формат</span>
                            <strong>{entry.format_label || 'Уточняется'}</strong>
                          </div>
                          <div className={styles.infoCard}>
                            <span>Локация</span>
                            <strong>{entry.location || (entry.link_url ? 'Онлайн-подключение' : 'Уточняется')}</strong>
                          </div>
                          <div className={styles.infoCard}>
                            <span>Дата и время</span>
                            <strong>{[entry.date_label, entry.time_label].filter(Boolean).join(' · ') || 'По согласованию'}</strong>
                          </div>
                        </div>
                        {entry.link_url ? (
                          <a href={entry.link_url} target="_blank" rel="noreferrer" className={styles.linkButton}>
                            Открыть ссылку на встречу
                          </a>
                        ) : (
                          <span className={`${styles.linkButton} ${styles.linkButtonDisabled}`} aria-disabled="true">
                            Открыть ссылку на встречу
                          </span>
                        )}
                        {entry.recording_url && (
                          <a
                            href={entry.recording_url}
                            target="_blank"
                            rel="noreferrer"
                            className={`${styles.linkButton} ${styles.quickOpen}`}
                            onClick={(e) => e.stopPropagation()}
                            title="Открыть ссылку на встречу"
                          >
                            Посмотреть запись
                          </a>
                        )}
                        {entry.homework_description ? (
                          <div className={styles.homeworkBlock}>
                            <strong>Задание</strong>
                            <p>{entry.homework_description}</p>
                          </div>
                        ) : null}
                        {/* {embedUrl ? (
                          <iframe
                            title={entry.title}
                            src={embedUrl}
                            className={styles.videoFrame}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          />
                        ) : null}
                        {entry.recording_url || entry.video_url ? (
                          <a
                            href={entry.recording_url || entry.video_url}
                            target="_blank"
                            rel="noreferrer"
                            className={styles.linkButtonSecondary}
                          >
                            Открыть запись
                          </a>
                        ) : (
                          <span className={`${styles.linkButtonSecondary} ${styles.linkButtonDisabled}`} aria-disabled="true">
                            Открыть запись
                          </span>
                        )} */}
                        {entry.recording_url || entry.video_url || entry.homework_description || entry.homework_template_url ? (
                          <button
                            type="button"
                            className={styles.linkButtonSecondary}
                            onClick={() => handleOpenArtifact(entry)}
                          >
                            Цифровой след
                          </button>
                        ) : null}
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>
          ))}
        </section>
      ) : null}

      {!loading && weekSections.length ? (
        <section className={styles.weeks}>
          {weekSections.map((week) => (
            <div key={week.key} className={styles.weekSection}>
              <div className={styles.weekHeader}>
                <div>
                  <h2 className={styles.weekTitle}>{week.label}</h2>
                  <p className={styles.weekRange}>{week.rangeLabel}</p>
                </div>
                <span className={styles.weekCount}>{week.items.length}</span>
              </div>

              <div className={styles.weekList}>
                {week.items.map(({ entry, dateMeta }) => {
                  const isOpen = openEntries.has(entry.entry_id);
                  const isPast = isPastEntry(dateMeta.parsedDate);

                  const borderColor = getFormatBorderColor(entry.format_label);
                  const cardStyle = borderColor ? { borderLeft: `8px solid ${borderColor}` } : {};

                  return (
                    <article
                      key={entry.entry_id}
                      className={`${styles.card} ${isOpen ? styles.cardOpen : ''} ${isPast ? styles.cardPast : ''}`}
                      id={normalizeTrackId(`${week.key}-${entry.track_label}-${entry.entry_id}`)}
                    >
                      <button
                        type="button"
                        className={styles.cardToggle}
                        onClick={() => toggleEntry(entry.entry_id)}
                        aria-expanded={isOpen}
                      >
                        <div className={styles.datePane}>
                          <span className={styles.weekday}>{dateMeta.weekdayLabel}</span>
                          <strong className={styles.dayNumber}>{dateMeta.dayLabel}</strong>
                          <span className={styles.monthLabel}>{dateMeta.monthLabel}</span>
                        </div>

                        <div style={cardStyle} className={styles.cardBody}>
                          {entry.presenter_name ? <span className={styles.speakerLine}>{entry.presenter_name}</span> : null}
                          <div className={styles.cardTopline}>
                            <div className={styles.cardMeta}>
                              <span className={styles.trackChip}>{entry.track_label || 'ЮФУ.Про'}</span>
                              {entry.is_control_event ? <span className={styles.controlEvent}>Контрольное мероприятие</span> : null}
                            </div>
                            <span className={styles.cardArrow} aria-hidden="true" />
                          </div>
                          <strong className={styles.cardTitle}>{entry.title}</strong>
                          <span className={styles.timeLabel}>
                            {entry.time_label || 'Время уточняется'}
                          </span>
                          {entry.description ? <p className={styles.cardLead}>{entry.description}</p> : null}
                        </div>
                      </button>

                      <div className={`${styles.cardDetails} ${isOpen ? styles.cardDetailsOpen : ''}`}>
                        <div className={styles.infoGrid}>
                          <div className={styles.infoCard}>
                            <span>Координатор</span>
                            <strong>{entry.presenter_name || 'Организаторы программы'}</strong>
                          </div>
                          <div className={styles.infoCard}>
                            <span>Формат</span>
                            <strong>{entry.format_label || 'Уточняется'}</strong>
                          </div>
                          <div className={styles.infoCard}>
                            <span>Локация</span>
                            <strong>{entry.location || (entry.link_url ? 'Онлайн-подключение' : 'Уточняется')}</strong>
                          </div>
                          <div className={styles.infoCard}>
                            <span>Дата и время</span>
                            <strong>{[entry.date_label, entry.time_label].filter(Boolean).join(' · ') || 'По согласованию'}</strong>
                          </div>
                        </div>
                        {entry.link_url ? (
                          <a href={entry.link_url} target="_blank" rel="noreferrer" className={styles.linkButton}>
                            Открыть ссылку на встречу
                          </a>
                        ) : (
                          <span className={`${styles.linkButton} ${styles.linkButtonDisabled}`} aria-disabled="true">
                            Открыть ссылку на встречу
                          </span>
                        )}
                        {entry.recording_url && (
                          <a
                            href={entry.recording_url}
                            target="_blank"
                            rel="noreferrer"
                            className={`${styles.linkButton} ${styles.quickOpen}`}
                            onClick={(e) => e.stopPropagation()}
                            title="Открыть ссылку на встречу"
                          >
                            Посмотреть запись
                          </a>
                        )}
                        {entry.homework_description ? (
                          <div className={styles.homeworkBlock}>
                            <strong>Задание</strong>
                            <p>{entry.homework_description}</p>
                          </div>
                        ) : null}
                        {/* {embedUrl ? (
                          <iframe
                            title={entry.title}
                            src={embedUrl}
                            className={styles.videoFrame}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          />
                        ) : null} */}
                        {/* {entry.recording_url || entry.video_url ? (
                          <a
                            href={entry.recording_url || entry.video_url}
                            target="_blank"
                            rel="noreferrer"
                            className={styles.linkButtonSecondary}
                          >
                            Открыть запись
                          </a>
                        ) : (
                          <span className={`${styles.linkButtonSecondary} ${styles.linkButtonDisabled}`} aria-disabled="true">
                            Открыть запись
                          </span>
                        )} */}
                        {/* {entry.recording_url || entry.video_url || entry.homework_description || entry.homework_template_url ? (
                          <button
                            type="button"
                            className={styles.linkButtonSecondary}
                            onClick={() => handleOpenArtifact(entry)}
                          >
                            Цифровой след
                          </button>
                        ) : null} */}
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>
          ))}
        </section>
      ) : null}
      <ArtifactModal
        isOpen={Boolean(artifactEntry)}
        onClose={() => setArtifactEntry(null)}
        entry={artifactEntry}
        contextId={artifactEntry?.entry_id}
      />
    </div>
  );
}
